/**
 * Personalised preview on a cart line (MFYEB-100).
 *
 * Draws the customer's name onto the blank product photo, in the cart, so what they see on the
 * bag is the thing they configured rather than a stock photograph of an unpersonalised product.
 *
 * NOTHING IS SAVED ANYWHERE. This redraws from what the line already carries — `_text_line_1`,
 * `_font`, `_font_colour` — plus the product's published config. So there is no upload, no stored
 * image, no extra Shopify object and no CORS dance, and a line added before this shipped renders
 * just as well as one added after. The saved image that Odoo and the confirmation email need is a
 * separate, server-side job: an email cannot run JavaScript, and the workshop must not stitch from
 * pixels a customer's browser produced.
 *
 * IT OVERLAYS THE THUMBNAIL RATHER THAN REPLACING IT. The markup keeps the normal product image
 * underneath and this fades in on top once it can actually draw. Any failure — no config, no blank
 * photo, no placement, a font with no file — leaves the customer looking at the ordinary product
 * photo, which is the same fallback philosophy as the product page.
 *
 * DELIBERATELY NOT AN ALPINE COMPONENT, unlike the product page's preview.
 *
 * It was one, and four bugs in a row came from that: two cart lines sharing state, drawing into
 * each other's canvas, stale text after a section swap, and finally the one that explained the
 * rest — `$el` is an Alpine MAGIC, not a property. It resolves to the element whose expression is
 * currently being evaluated, so outside an Alpine-evaluated expression it is not dependably the
 * component's own root. This code draws from a document event listener, a MutationObserver and an
 * awaited fetch, none of which Alpine knows about, so it was reading another line's element and two
 * provably separate instances always held the same name.
 *
 * A cart line has no interactivity and nothing to react to: it is drawn once and redrawn if its
 * line changes. So there is no component and no instance state at all. Every function here takes
 * the ELEMENT and reads everything from it, which makes "which line is this?" unanswerable
 * incorrectly. The product page's preview keeps Alpine, and should: it is a live editor with real
 * state, and its `$el` reads happen inside Alpine expressions.
 *
 * ONE FETCH PER PRODUCT, NOT PER LINE. A cart can hold the same product several times (two names,
 * two sizes), and this page already makes a lot of requests. Config fetching, font registration and
 * text measurement all come from `PimConfig` (assets/personalisation-config.js), which caches each
 * per page, so five lines of one product share one request and one FontFace — and share them with
 * the product page's preview too, rather than keeping a second cache of the same things.
 */
if (!window.Eurus) window.Eurus = {};
if (!window.Eurus.loadedScript) window.Eurus.loadedScript = new Set();

if (!window.Eurus.loadedScript.has('personalisation-cart-preview.js')) {
  window.Eurus.loadedScript.add('personalisation-cart-preview.js');

  (function () {
    /** Bumped when this file changes, so a stale asset can be told apart from a code fault. */
    const VERSION = 'cart-preview-2026-10-28';

    /** Every personalised cart line, on the cart page and in the drawer alike. */
    const SELECTOR = '[data-config-url][data-texts]';

    /**
     * Per-element bookkeeping, kept ON the element.
     *
     * Not in a Map keyed by element, because a section swap discards the nodes and a Map would hold
     * them alive; not in module state, because that is what shared bugs are made of. When the node
     * goes, its bookkeeping goes with it.
     */
    const STATE = '__pimCartPreview';

    function stateFor(el) {
      if (!el[STATE]) {
        el[STATE] = {
          version: VERSION,
          /**
           * Why this line is not showing a preview, or 'drawn' when it is.
           *
           * Recorded rather than logged because every failure here is SILENT by design (the product
           * photo stays visible), which cost three rounds of blind debugging before it existed:
           *
           *   PimCartPreview.report()
           */
          bail: 'not started',
          reads: 0,
          /** Guards against an older draw finishing after a newer one. See draw(). */
          seq: 0,
        };
      }

      return el[STATE];
    }

    /**
     * Everything needed to draw one line, read off its element every time.
     *
     * The element's attributes are the truth: Liquid renders them per cart line. Holding a copy
     * anywhere else is what made a line draw its neighbour's name.
     */
    function readInputs(el) {
      const state = stateFor(el);
      let texts = {};

      try {
        texts = JSON.parse(el.dataset.texts || '{}');
      } catch (e) {
        texts = {};
      }

      let lineStyles = {};
      try {
        lineStyles = JSON.parse(el.dataset.lineStyles || '{}');
      } catch (e) {
        lineStyles = {};
      }

      state.reads++;

      return {
        configUrl: el.dataset.configUrl || null,
        variantId: el.dataset.variantId || null,
        font: el.dataset.font || null,
        colour: el.dataset.colour || null,
        lineStyles: lineStyles,
        texts: texts,
        arcIdBase: (canvasOf(el) || { dataset: {} }).dataset.arcIdBase || 'pim-cart-arc',
      };
    }

    /**
     * This line's drawing surface: a DIRECT child only.
     *
     * A plain `querySelector` returns the first match anywhere below, so if the cart markup ever
     * nests one overlay inside another, the outer line would draw into the inner one's canvas and
     * both would show the outer name.
     */
    function canvasOf(el) {
      return el.querySelector(':scope > [data-arc-id-base]') || el.querySelector('[data-arc-id-base]');
    }

    function hasText(texts) {
      return Object.keys(texts).some((k) => String(texts[k] || '').trim() !== '');
    }

    /** Fade in, using the class the MARKUP names — see the note in the snippet for why. */
    function reveal(el) {
      const readyClass = el.dataset.readyClass || 'opacity-100';
      el.classList.add(readyClass);
      el.classList.remove('opacity-0');
    }

    /** Back to the ordinary product photo. */
    function hide(el) {
      el.classList.remove(el.dataset.readyClass || 'opacity-100');
      el.classList.add('opacity-0');
    }

    /**
     * Draw one line. The element is the only input; there is no instance to be attached to the
     * wrong one.
     *
     * Every early return leaves the ordinary product photo showing and records why.
     */
    /** What this line is drawn FROM: change any of it and the picture has to change. */
    function signature(input) {
      return JSON.stringify([input.texts, input.font, input.colour, input.variantId, input.lineStyles]);
    }

    /** A draw that was superseded by a newer one. Not a refusal, and must not clear anything. */
    const STALE = { stale: true };

    /**
     * Draw one line. The element is the only input; there is no instance to be attached to the
     * wrong one.
     *
     * SKIPS WORK IT HAS ALREADY DONE. A scan runs on every cart change and on every insertion, so
     * without this a bag with five personalised lines re-measures and re-serialises five SVGs on
     * each quantity change, for an identical result. Comparing what the line is drawn FROM is
     * cheaper than drawing it.
     *
     * AND CLEARS A PICTURE THAT HAS GONE WRONG. If a line's values change and the redraw then
     * fails, what is on screen was drawn from the OLD values — a wrong picture, which is the one
     * thing this feature must never show. In that case the overlay is emptied and hidden so the
     * ordinary product photo comes back. A failure that changes nothing (a redraw of the same
     * values) leaves the existing drawing alone, because it is still correct.
     */
    async function draw(el) {
      const state = stateFor(el);
      /**
       * Two draws can be in flight at once: a cart event and this element's own attribute change
       * can land together, and the fetches in between are awaited. Without this, the SLOWER draw
       * writes last and can put back what the newer one just corrected.
       */
      const seq = ++state.seq;
      const stale = () => state.seq !== seq;

      const input = readInputs(el);
      const wanted = signature(input);

      if (state.drawn === wanted) {
        state.bail = 'drawn';

        return;
      }

      const refusal = await paint(el, input, state, stale);

      if (refusal === STALE || stale()) {
        return;
      }

      if (refusal) {
        state.bail = refusal;

        // What is on screen was drawn from values this line no longer has.
        if (state.drawn) {
          const layer = canvasOf(el);
          if (layer) layer.innerHTML = '';
          hide(el);
          state.drawn = null;
        }

        return;
      }

      state.drawn = wanted;
      state.bail = 'drawn';
      reveal(el);
    }

    function isTextPlacement(placement) {
      return !!placement && (!placement.type || placement.type === 'text');
    }

    /** The component step that draws this text line on its own, preferring the one the line is routed to. */
    function ownerStep(preview, options, key) {
      const line = (options.text_lines || []).find((l) => l && l.key === key) || {};
      const routed = Array.isArray(line.bundle_component_ids) ? line.bundle_component_ids.map(Number) : [];
      const drawing = (preview.steps || [])
        .slice()
        .sort((a, b) => (Number(a && a.sort_order) || 0) - (Number(b && b.sort_order) || 0))
        .filter(
          (s) =>
            s &&
            s.bundle_component_id != null &&
            window.PimPreviewGeometry.resolveViews(s).some((v) =>
              (v.placements || []).some((p) => isTextPlacement(p) && p.text_line_key === key)
            )
        );

      return drawing.find((s) => routed.indexOf(Number(s.bundle_component_id)) !== -1) || drawing[0] || null;
    }

    /** A placement draws with its owning component's lists and method, and its line's `_font:text_line_N` pick. */
    function placementStyle(placement, step, config, input) {
      const options = config.options || {};
      const styles = input.lineStyles || {};
      const key = placement.text_line_key || null;
      const code = key ? String(key).replace(/^_/, '') : null;
      const owner = (step.bundle_component_id == null && key && ownerStep(config.preview, options, key)) || step;
      const listOf = (field, fallback) => (Array.isArray(owner[field]) ? owner[field] : fallback);

      return {
        fonts: listOf('fonts', options.fonts),
        colours: listOf('font_colours', options.font_colours),
        font: (code && styles['_font:' + code]) || input.font,
        colour: (code && styles['_font_colour:' + code]) || input.colour,
        defaultHex: owner.default_colour_hex || config.preview.default_colour_hex,
        method: owner.method || step.method || config.preview.method,
      };
    }

    /**
     * The drawing itself. Returns null when it drew, a reason when it refused, or STALE when a
     * newer draw has started. Every refusal leaves the ordinary product photo showing.
     */
    async function paint(el, input, state, stale) {
      const geometry = window.PimPreviewGeometry;

      if (!geometry) {
        return 'PimPreviewGeometry not loaded';
      }

      if (!window.PimConfig || !window.PimConfig.fetchConfig) {
        return 'PimConfig missing or stale (no fetchConfig)';
      }

      if (!input.configUrl || !hasText(input.texts)) {
        return 'no config url or no text';
      }

      state.bail = 'fetching config';
      const config = await window.PimConfig.fetchConfig(input.configUrl);
      if (stale()) return STALE;

      if (!config) {
        return 'config did not load';
      }
      if (!config.preview || config.preview.enabled === false) {
        return 'product has no preview block (not set up for the new UI)';
      }

      const step = geometry.resolveStep(config.preview, null);
      if (!step) {
        return 'no renderable step in the config';
      }

      const image = geometry.resolveBaseImage(step, input.variantId);
      if (!image) {
        return 'no blank photo for this variant or default';
      }

      const options = config.options || {};
      const resolved = new Map();

      // Each name in a group shot is drawn in its own component's font, colour and method, and
      // every font has to load: one that cannot would draw that name in the browser's default serif.
      for (const placement of step.placements || []) {
        if (!isTextPlacement(placement) || !geometry.textForPlacement(placement, { texts: input.texts, text: '' })) continue;
        const style = placementStyle(placement, step, config, input);
        const fontChoice = geometry.resolveFontChoice(style.fonts, style.font);

        if (!fontChoice) {
          return 'no font in the config at all (line asked for "' + style.font + '")';
        }
        if (!(await window.PimConfig.registerFont(fontChoice))) {
          return 'font file would not load: ' + fontChoice.font_file_url;
        }
        if (stale()) return STALE;

        const family = window.PimConfig.fontFamilyFor(fontChoice.value);
        resolved.set(placement, {
          fontFamily: family,
          fill: geometry.resolveFill(style.colours, style.colour, style.defaultHex),
          method: style.method,
          measure: (t, size) => window.PimConfig.measureWidth(t, size, family),
          measureCapHeight: (size) => window.PimConfig.measureCapHeight(size, family),
          measureInkHeight: (size) => window.PimConfig.measureInkHeight(size, family),
        });
      }

      const result = geometry.buildSvg({
        image: image,
        placements: step.placements,
        texts: input.texts,
        text: '',
        // The drawn font size is settled against each field's character limit, not against the
        // name, so a cart line has to pass them or it draws the SAME name at a different size from
        // the product page it was configured on. See `constantEmSize` in the geometry module.
        charLimits: geometry.charLimitsFrom(options),
        method: step.method || config.preview.method,
        styleFor: (placement) => resolved.get(placement),
        arcPathId: input.arcIdBase,
      });

      const layer = canvasOf(el);
      if (!layer) {
        return 'canvas element missing from the overlay';
      }

      layer.innerHTML = result.markup;

      // Fill the thumbnail the way the product photo underneath does. buildSvg sizes its root
      // svg at 100% but leaves the default `meet` scaling, which letterboxes a square-ish blank
      // photo inside a differently proportioned cart thumbnail and shows the stock photo through
      // the gaps. `slice` crops instead, matching the object-cover the theme uses on the <img>.
      const svg = layer.querySelector('svg');
      if (svg) svg.setAttribute('preserveAspectRatio', 'xMidYMid slice');

      return null;
    }

    /**
     * Redraw a line whose own attributes change.
     *
     * Only `data-*` are watched, so the class this adds on success cannot feed the observer. One
     * observer per personalised line, and it stops when its element leaves the document — Alpine
     * aside, an innerHTML swap runs no teardown of any kind, so nothing else would stop it.
     */
    function watch(el) {
      if (typeof MutationObserver === 'undefined') return;

      const observer = new MutationObserver(() => {
        if (!el.isConnected) {
          observer.disconnect();
          return;
        }
        draw(el);
      });

      observer.observe(el, {
        attributes: true,
        attributeFilter: ['data-texts', 'data-font', 'data-colour', 'data-variant-id', 'data-line-styles'],
      });

      stateFor(el).observer = observer;
    }

    /** Set one line up (once) and draw it. */
    function mount(el) {
      if (!el[STATE]) {
        stateFor(el);
        // One observer per element, however many times it is mounted.
        watch(el);
      }
      draw(el);
    }

    /**
     * Find every personalised line and draw it.
     *
     * Safe to call as often as you like: a line already drawn from unchanged attributes just draws
     * the same thing again, and the shared config and font caches mean it costs no requests.
     */
    function scan() {
      document.querySelectorAll(SELECTOR).forEach(mount);
    }

    /**
     * DRAW LINES AS THEY ARRIVE IN THE DOM. This is the primary trigger, and it has to be.
     *
     * Cart lines are server-rendered HTML that the theme inserts whenever it feels like it: the cart
     * page and the drawer are refreshed by replacing a section's innerHTML, and there is more than
     * one path that does it. Listening for the theme's own cart events was tried first and was NOT
     * enough — the drawer's lines appeared with `bail`, `reads` and `version` all undefined, meaning
     * no scan had ever visited them, so whichever path inserted them fired none of the three events
     * this file knows about.
     *
     * Watching the document instead makes the trigger independent of the theme's event vocabulary,
     * which is the point: a preview that only appears when some other script remembers to announce
     * itself is a preview that will silently stop appearing again.
     *
     * The callback runs on every DOM insertion on the page, so it stays cheap: an element-node
     * check, then `matches` on the node and a scoped query inside it. `mount` is idempotent, so a
     * node that arrives twice costs one lookup.
     */
    function watchDocument() {
      if (typeof MutationObserver === 'undefined' || !document.body) return;

      new MutationObserver((records) => {
        records.forEach((record) => {
          record.addedNodes.forEach((node) => {
            if (node.nodeType !== 1) return;
            if (node.matches && node.matches(SELECTOR)) mount(node);
            if (node.querySelectorAll) node.querySelectorAll(SELECTOR).forEach(mount);
          });
        });
      }).observe(document.body, { childList: true, subtree: true });
    }

    /**
     * The theme's cart events are kept as a BELT, not the primary trigger: they cover a swap that
     * reuses nodes and only changes what is inside them, which no insertion observer would see.
     * `personalised-cards-shopify.js` re-scans on the same events and explains why both names are
     * needed — `cart:sections:swapped` fires on quantity changes where `cart:refresh` does not.
     */
    ['cart:refresh', 'cart:sections:swapped', 'eurus:cart:items-changed'].forEach((name) =>
      document.addEventListener(name, scan)
    );

    function start() {
      watchDocument();
      scan();
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start);
    } else {
      start();
    }

    /**
     * One call that answers "why is this line not showing a preview?".
     *
     * Every failure above is silent by design, so this is the difference between an answer and
     * another round of blind debugging. Keep it.
     */
    window.PimCartPreview = {
      version: VERSION,
      scan: scan,
      draw: draw,
      report: () =>
        Array.from(document.querySelectorAll(SELECTOR)).map((el, n) => ({
          n: n,
          bail: (el[STATE] || {}).bail,
          reads: (el[STATE] || {}).reads,
          version: (el[STATE] || {}).version,
          attr: el.dataset.texts,
          drawn: Array.from(el.querySelectorAll('text')).map((t) => t.textContent).join('|'),
        })),
    };
  })();
}
